/* eslint-disable no-return-assign */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import React, { useContext, useState, useEffect } from "react";
import { Box } from "@mui/material";
import * as Sentry from "@sentry/react";
import EditIcon from "@mui/icons-material/Edit";
import Styles from "./styles";
import Button from "../../../components/common/Button";
import ControlPanelAPI from "../../../services/api/controlpanel";
import { ToastAlertContext } from "../../../config/providers/ToastAlert";
import { AlertStatus, PERMISSIONS } from "../../../contants";
import Loader from "../../../components/common/Loader/Loader";
import useUserConfig from "../../../hooks/useUserConfig";
import SettingModal from "./SettingModal";

function ControlPanel() {
  const toast = useContext(ToastAlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const hasPermission = useUserConfig();

  const [settings, setSettings] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [grouped, setGrouped] = useState({});

  const fetchSettings = async () => {
    try {
      const res = await ControlPanelAPI.getAllSettings();
      console.log("fetch Data", res);
      // Adjust based on actual structure
      if (res.data && Array.isArray(res.data)) {
        setSettings(res.data); // If res.data is already the array
      } else if (res.data && Array.isArray(res.data.data)) {
        setSettings(res.data.data); // If res.data has .data array
      } else {
        setSettings([]);
      }
    } catch (err) {
      toast.show("Failed to fetch settings", AlertStatus.ERROR);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (settings && Array.isArray(settings)) {
      const data = settings.reduce((acc, item) => {
        const section = item.key.split("_")[0];
        if (!acc[section]) acc[section] = [];
        acc[section].push(item);
        return acc;
      }, {});
      setGrouped(data);
    }
  }, [settings]);

  const dataTypeColors = {
    boolean: "#16a34a", // green
    varchar: "#2563eb", // blue
    int: "#d97706", // amber
    json: "#7c3aed", // purple
    default: "#6b7280", // gray
  };

  const handleSyncRolesAndPermission = async () => {
    try {
      setIsLoading(true);
      const response = await ControlPanelAPI.syncRolesAndPermission();
      if (response.code === 200) {
        toast.show(
          "Roles and Permissions synced successfully",
          AlertStatus.SUCCESS
        );
      }
    } catch (error) {
      toast.show(
        "Something went wrong. Please contact support.",
        AlertStatus.ERROR
      );
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAttendanceUnmarkedEmailer = async () => {
    try {
      setIsLoading(true);
      const response = await ControlPanelAPI.emailUnmarkedStructures();
      if (response.code === 200) {
        toast.show("Email sent successfully", AlertStatus.SUCCESS);
      }
    } catch (error) {
      toast.show(
        "Something went wrong. Please contact support.",
        AlertStatus.ERROR
      );
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleEdit = (setting) => {
    setEditData(setting);
    setModalOpen(true);
  };

  const handleSubmit = async (data) => {
    try {
      if (editData) {
        await ControlPanelAPI.updateSetting(editData.id, data);
        toast.show("Setting updated successfully", AlertStatus.SUCCESS);
      } else {
        await ControlPanelAPI.createNewSetting(data);
        toast.show("Setting added successfully", AlertStatus.SUCCESS);
      }
      fetchSettings();
    } catch (err) {
      toast.show("Failed to save setting", AlertStatus.ERROR);
    }
  };

  const buttonConfigs = [
    {
      label: "Sync Roles and Permissions",
      style: Styles.syncRolesPermissions,
      width: "30%",
      onClick: handleSyncRolesAndPermission,
    },
    ...(hasPermission(PERMISSIONS.UNMARKED_STRUCTURE_EMAIL)
      ? [
          {
            label: "Attendance Unmarked Emailer",
            style: Styles.attendanceUnmarkedEmailer,
            width: "30%",
            onClick: handleAttendanceUnmarkedEmailer,
          },
        ]
      : []),
  ];

  return (
    <div style={Styles.container}>
      <div>
        <Box>
          <Box
            sx={{
              ...Styles.boxStyle,
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {buttonConfigs.map((config, index) => (
              <Button
                key={index}
                variant="contained"
                label={config.label}
                style={{
                  ...Styles.buttonBaseStyle,
                  ...config.style,
                  width: config.width,
                }}
                onClick={config.onClick}
              />
            ))}
            <Button
              variant="contained"
              label="Add Setting"
              onClick={handleAdd}
              style={{ marginLeft: "auto" }}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Box
            sx={{
              borderRadius: "14px",
              overflow: "hidden",
              background: "#ffffff", // <--- black removed
              border: "1px solid #1E293B",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ color: "#01050cff" }}>
                  <th style={{ padding: "12px", textAlign: "left" }}>#</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Key</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Value</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Type</th>
                  <th style={{ padding: "12px", textAlign: "center" }}>
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {Object.keys(grouped).map((sec) => (
                  <React.Fragment key={sec}>
                    {/* SECTION HEADER */}

                    {grouped[sec].map((s, idx) => (
                      <tr
                        key={s.id}
                        style={{
                          borderBottom: "1px solid #1F2937",
                          color: "#01050cff",
                          transition: "0.2s",
                        }}
                        // eslint-disable-next-line no-return-assign
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                        // eslint-disable-next-line no-return-assign
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        {/* SERIAL */}
                        <td style={{ padding: "12px" }}>{idx + 1}</td>

                        {/* KEY */}
                        <td style={{ padding: "12px", fontWeight: 500 }}>
                          {s.key}
                        </td>

                        {/* VALUE */}
                        <td style={{ padding: "12px" }}>
                          {s.dataType === "boolean" ? (
                            <span
                              style={{
                                padding: "4px 10px",
                                borderRadius: "6px",
                                fontSize: "13px",
                                color: "#fff",
                                background:
                                  s.value === "true"
                                    ? "rgba(34,197,94,0.7)"
                                    : "rgba(239,68,68,0.7)",
                                boxShadow:
                                  s.value === "true"
                                    ? "0 0 8px #22c55e88"
                                    : "0 0 8px #ef444488",
                              }}
                            >
                              {s.value === "true" ? "ACTIVE" : "INACTIVE"}
                            </span>
                          ) : (
                            <span style={{ opacity: 0.9 }}>{s.value}</span>
                          )}
                        </td>

                        {/* DATA TYPE COLOR BADGE */}
                        <td style={{ padding: "12px" }}>
                          <span
                            style={{
                              padding: "4px 10px",
                              borderRadius: "6px",
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "#fff",
                              textTransform: "uppercase",
                              background:
                                dataTypeColors[s.dataType] ||
                                dataTypeColors.default,
                              boxShadow: `0 0 8px ${
                                dataTypeColors[s.dataType] ||
                                dataTypeColors.default
                              }80`,
                            }}
                          >
                            {s.dataType}
                          </span>
                        </td>

                        {/* ACTION ICON */}
                        <td style={{ textAlign: "center", padding: "12px" }}>
                          <button
                            type="button"
                            onClick={() => handleEdit(s)}
                            style={{
                              cursor: "pointer",
                              display: "inline-flex",
                              padding: "6px",
                              borderRadius: "8px",
                              background: "rgba(217, 220, 230, 0.3)",
                              color: "#60A5FA",
                              transition: "0.2s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background =
                                "rgba(241, 243, 247, 0.4)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background =
                                "rgba(245, 246, 248, 0.3)")
                            }
                          >
                            <EditIcon style={{ fontSize: "20px" }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>

        {modalOpen && (
          <SettingModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            onSubmit={handleSubmit}
            initialData={editData}
          />
        )}
      </div>
      {isLoading && <Loader />}
    </div>
  );
}

ControlPanel.propTypes = {};

export default ControlPanel;
